import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';

export function Preferences() {
  const { preferences, updatePreferences } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleToggle = async (key: string, value: boolean | string | number) => {
    await updatePreferences({ [key]: value });
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain at least one number');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await api.post('/api/auth/update-password', {
        currentPassword,
        newPassword,
      });

      if (response.success) {
        setPasswordSuccess('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(response.error || 'Failed to update password');
      }
    } catch {
      setPasswordError('Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Preferences</h1>
        <p className="text-foreground-muted">Customize your Tool Crib experience</p>
      </div>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>Notifications</CardHeader>
        <CardBody className="space-y-4">
          <ToggleRow
            label="Low Stock Alerts"
            description="Notify when tools fall below minimum quantity"
            checked={preferences?.notifyLowStock ?? true}
            onChange={(v) => handleToggle('notifyLowStock', v)}
          />
          <ToggleRow
            label="Critical Stock Alerts"
            description="Escalated alerts for critical shortages"
            checked={preferences?.notifyCriticalStock ?? true}
            onChange={(v) => handleToggle('notifyCriticalStock', v)}
          />
          <ToggleRow
            label="End of Life Alerts"
            description="Notify when tools approach expected life limit"
            checked={preferences?.notifyEndOfLife ?? true}
            onChange={(v) => handleToggle('notifyEndOfLife', v)}
          />
          <ToggleRow
            label="Email Notifications"
            description="Receive notifications via email"
            checked={preferences?.emailNotifications ?? true}
            onChange={(v) => handleToggle('emailNotifications', v)}
          />
          <ToggleRow
            label="SMS Critical Alerts"
            description="Receive text messages for critical alerts"
            checked={preferences?.smsCriticalAlerts ?? false}
            onChange={(v) => handleToggle('smsCriticalAlerts', v)}
          />
        </CardBody>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>Change Password</CardHeader>
        <CardBody>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {passwordError && (
              <div className="p-3 bg-danger/10 text-danger rounded-md text-sm">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-success/10 text-success rounded-md text-sm">
                {passwordSuccess}
              </div>
            )}
            <Input
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <Input
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <Input
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <p className="text-sm text-foreground-muted">
              Password must be at least 8 characters with uppercase, lowercase, and a number.
            </p>
            <Button type="submit" disabled={isChangingPassword}>
              {isChangingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardBody>
      </Card>

      {/* Display Preferences */}
      <Card>
        <CardHeader>Display</CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Label Format</p>
              <p className="text-sm text-foreground-muted">Preferred format for generated labels</p>
            </div>
            <Select
              value={preferences?.preferredLabelFormat ?? 'qr'}
              onChange={(e) => handleToggle('preferredLabelFormat', e.target.value as any)}
              options={[
                { value: 'qr', label: 'QR Code' },
                { value: 'barcode', label: 'Barcode' },
              ]}
              className="w-32"
            />
          </div>
          <ToggleRow
            label="Human-Readable IDs"
            description="Show text IDs alongside barcodes on labels"
            checked={preferences?.showHumanReadableId ?? true}
            onChange={(v) => handleToggle('showHumanReadableId', v)}
          />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Items Per Page</p>
              <p className="text-sm text-foreground-muted">Number of items to show in lists</p>
            </div>
            <Select
              value={String(preferences?.itemsPerPage ?? 25)}
              onChange={(e) => handleToggle('itemsPerPage', parseInt(e.target.value) as any)}
              options={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              className="w-24"
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-foreground-muted">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-secondary'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
